import { UseEntityData } from "crqlar-react-common/dist/components/useEntity";
import { GastrodatConfig } from "crqlar-react-common/dist/models/integrations/gastrodat/gastrodat.config";
import { useTranslation } from "react-i18next";
import { Alert, Autocomplete, Divider, Stack, Switch } from "@mui/material";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import { useAllAgeCategories } from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import { Sector } from "crqlar-react-common/dist/models/sector/sector.model";
import { useAllSectors } from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
  gastrodatConfigData: UseEntityData<GastrodatConfig>;
}

const GastrodatConfigForm = ({ gastrodatConfigData }: Props) => {
  const { t } = useTranslation();

  const sectors: Sector[] = useAllSectors();
  const ageCategories = useAllAgeCategories();

  const gastrodatConfig = gastrodatConfigData.item;
  const nameMissing = isRequiredTextMissing(gastrodatConfigData, gastrodatConfig.name);
  const setGastrodatConfig = gastrodatConfigData.setItem;

  const updateField = (field: keyof GastrodatConfig, value: any) => {
    setGastrodatConfig({ ...gastrodatConfig, [field]: value });
  };

  let integrationUI;
  if (gastrodatConfig.id) {
    integrationUI =
    <>
                <Property
        title={t('Username')}
        tooltip={t('Generated username for authenticating Gastrodat import requests.')}>

                    <TextField
          id={'username'}
          size={'small'}
          value={gastrodatConfig.id || ''}
          fullWidth
          disabled />

                </Property>

                <Property
        title={t('Password / Auth token')}
        tooltip={t('Generated token used as the password for Gastrodat import requests.')}>

                    <TextField
          id={'token'}
          size={'small'}
          value={gastrodatConfig.authorizationToken || ''}
          fullWidth
          disabled />

                </Property>

                <Property
        title={t('Integration instructions')}
        tooltip={t('Endpoint and authentication details for sending data into this connector.')}>

                    <Stack gap={1}>
                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            <Typography>
                                {t("Send")} <strong>{t("POST")}</strong> with XML body
                                to <strong>{process.env.REACT_APP_API_ENDPOINT}{t("/v3/integrations/gastrodat/import")}</strong>
                            </Typography>
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            <Typography>
                                {t("Use")} <strong>{t("Basic Auth")}</strong> {t("with provided username and password for authorization")}
                            </Typography>
                        </Alert>
                    </Stack>

                </Property>
            </>;

  } else {
    integrationUI =
    <Alert
      variant={'outlined'}
      severity={'info'}
      sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                {t('Please save the Gastrodat configuration first to see the integration details')}
            </Alert>;

  }

  const credentialsSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Name')}
        tooltip={t('Internal label for identifying this Gastrodat integration configuration.')}>

                    <TextField
          id={'name'}
          size={'small'}
          value={gastrodatConfig.name || ''}
          error={nameMissing}
          onChange={(e) => updateField('name', e.target.value)}
          fullWidth
          required />

                </Property>

                {integrationUI}
            </Stack>
        </Stack>;


  const configurationSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Enabled')}
        tooltip={t('Turns the Gastrodat integration on or off.')}>

                    <Switch
          id={'enabled'}
          checked={gastrodatConfig.enabled}
          onChange={(e) => updateField('enabled', e.target.checked)} />

                </Property>

                <Property
        title={t('Property ID / Name (optional, multiproperty)')}
        tooltip={t('Optional property identifier assigned to all data imported through this connector.')}>

                    <Stack
          width={'100%'}
          gap={1}>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'error'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
            id={'name'}
            size={'small'}
            value={gastrodatConfig.propertyId || ''}
            onChange={(e) => updateField('propertyId', e.target.value)}
            fullWidth
            required />

                    </Stack>

                </Property>

                <Property
        title={t('Accommodation sector')}
        tooltip={t('Sector where imported accommodation reservations and resources should be mapped.')}>

                    <Autocomplete
          id={'sectors'}
          options={sectors}
          getOptionLabel={(option: any) => option.name || ''}
          value={sectors.find((s) => s.id === gastrodatConfig.accommodationSectorId) || null}
          renderInput={(params) =>
          <TextField
            {...params}
            fullWidth
            size={'small'} />

          }
          fullWidth
          onChange={(e, value) => {
            updateField('accommodationSectorId', value?.id);
          }} />

                </Property>

                <Property
        title={t('Adults age category')}
        tooltip={t('Age category used for adult guest counts imported from Gastrodat.')}>

                    <Autocomplete
          id={'adults-category-id'}
          options={ageCategories}
          getOptionLabel={(option: any) => option.name || ''}
          value={ageCategories.find((s) => s.id === gastrodatConfig.adultsCategoryId) || null}
          renderInput={(params) =>
          <TextField
            {...params}
            fullWidth
            size={'small'} />

          }
          fullWidth
          onChange={(e, value) => {
            updateField('adultsCategoryId', value?.id);
          }} />

                </Property>

                <Property
        title={t('Children age category')}
        tooltip={t('Age category used for child guest counts imported from Gastrodat.')}>

                    <Autocomplete
          id={'children-category-id'}
          options={ageCategories}
          getOptionLabel={(option: any) => option.name || ''}
          value={ageCategories.find((s) => s.id === gastrodatConfig.childrenCategoryId) || null}
          renderInput={(params) =>
          <TextField
            {...params}
            fullWidth
            size={'small'} />

          }
          fullWidth
          onChange={(e, value) => {
            updateField('childrenCategoryId', value?.id);
          }} />

                </Property>
            </Stack>
        </Stack>;


  return (
    <Stack
      sx={{
        width: '100%',
        gap: 6
      }}>

            {credentialsSection}
            <Divider />
            {configurationSection}
        </Stack>);

};

export default GastrodatConfigForm;
